import { Smoother } from 'core/Smooth';

const defaultOptions = {
  startAt: 0,
  groupBy: 1,
  autoLayout: true,
  breakpoints: [],
  dragMultiplier: 1.8,
  onBreakpointChange: () => {},
};

class Carousel {
  constructor(options) {
    this.bind();

    const { $element } = options;
    delete options.$element;

    this.options = Object.assign({}, defaultOptions, options);
    this.$element = $element;
    this.$wrapper = $element.querySelector('.carousel__wrapper');
    this.$items = [...$element.querySelectorAll('.carousel__item')];
    this.$controls = [...$element.querySelectorAll('.carousel__control')];
    this.$dots = [...$element.querySelectorAll('.carousel__dots button')];
    this.$zones = [...$element.querySelectorAll('.carousel__zones div')];
    this.$scrollbar = $element.querySelector('.carousel__scrollbar__container');
    this.length = this.$items.length;
    this.mediaQueryLists = this.options.breakpoints.map((bp) => window.matchMedia(bp));
    this.observer = new IntersectionObserver(this.handleIntersection, {
      threshold: 0.0,
      rootMargin: '0px 200px 0px 200px',
    });

    this.index = this.options.startAt;

    this.items = this.$items.map(($node, index) => {
      const naturalWidth = parseInt($node.dataset.width, 10);
      const naturalHeight = parseInt($node.dataset.height, 10);
      return {
        $node,
        naturalWidth,
        naturalHeight,
        ratio: naturalWidth / naturalHeight,
      };
    });

    this.smooth = Smoother.add({
      precision: 4,
      smooth: 0.08,
      max: this.items.length - 1,
      min: 0,
      resistance: 100,
    });

    if (this.options.autoLayout) {
      this.$wrapper.style.width = `${this.length * 100}%`;
      this.$items.forEach(($item) => ($item.style.width = `${100 / this.length}%`));
    }

    this.handleBreakpoint();
    this.handleResize();
    this.enable();
    this.goTo(this.index, true);
  }

  bind() {
    this.handleEvent = this.handleEvent.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleUpdateEnd = this.handleUpdateEnd.bind(this);
    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleResized = this.handleResized.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleClickControl = this.handleClickControl.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleClickDot = this.handleClickDot.bind(this);
    this.handleClickZone = this.handleClickZone.bind(this);
    this.goTo = this.goTo.bind(this);
  }

  enable() {
    this.events(true);
  }

  disable() {
    this.events(false);
  }

  events(isEnabled) {
    if (this.isEnabled === isEnabled) return;
    const listener = isEnabled ? 'addListener' : 'removeListener';
    const eventListener = isEnabled ? 'addEventListener' : 'removeEventListener';
    const observeListener = isEnabled ? 'observe' : 'unobserve';

    // Events
    window[eventListener]('resize', this.handleResize);
    window[eventListener]('keyup', this.handleKeyUp);
    ['mousedown', 'touchstart'].forEach((event) =>
      this.$element[eventListener](event, this.handleEvent, { passive: true })
    );
    this.$controls.forEach(($control) => $control[eventListener]('click', this.handleClickControl));
    this.$dots.forEach(($dot) => $dot[eventListener]('click', this.handleClickDot));
    this.$zones.forEach(($zone) => $zone[eventListener]('click', this.handleClickZone));

    // Gotos
    this.$items.forEach(($item, index) => {
      if ($item.id) {
        const $goto = document.querySelector(`[data-carousel-goto="${$item.id}"]`);
        if ($goto) {
          $goto[eventListener]('click', () => this.goTo(index));
        }
      }
    });

    // Smooth
    this.smooth[listener]('update', this.handleUpdate);
    this.smooth[listener]('end', this.handleUpdateEnd);

    // Breakpoints
    this.mediaQueryLists.forEach((mql) => mql[listener](this.handleBreakpoint));

    // Observer
    this.$items.forEach(($item) => this.observer[observeListener]($item));

    this.isEnabled = isEnabled;
  }

  destroy() {
    this.disable();
    this.observer.disconnect();
    this.$wrapper.style.removeProperty('width');
    this.$items.forEach(($item) => {
      $item.style.removeProperty('width');
      $item.style.removeProperty('transform');
    });
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.style.removeProperty('visibility');
      } else {
        entry.target.style.visibility = 'hidden';
      }
    });
  }

  handleClickControl(event) {
    const { direction } = event.currentTarget.dataset;
    this.goTo(this.index + parseInt(direction, 10));
  }

  handleClickDot() {
    const { index } = event.currentTarget.dataset;
    this.goTo(parseInt(index, 10));
  }

  handleClickZone(event) {
    const direction = event.target.classList.contains('carousel__zones__left') ? -1 : 1;
    this.goTo(this.index + direction);
  }

  handleEvent(event) {
    const { target: $node, changedTouches, screenX, type } = event;
    const x = changedTouches && changedTouches.length > 0 ? changedTouches[0].screenX : screenX;
    const y = changedTouches && changedTouches.length > 0 ? changedTouches[0].screenY : screenY;

    const isTargetLink = $node.tagName === 'A';
    const isLink = isTargetLink || $node.closest('a') !== null;
    if (isLink) {
      event.preventDefault();
    }

    switch (type) {
      case 'mousedown':
      case 'touchstart':
        if (isLink) {
          this.downTime = Date.now();
          this.downLink = isTargetLink ? $node : $node.closest('a');
        }

        this.downTarget = this.smooth.target;
        this.downX = x;
        this.downY = y;
        this.bindDownEvents(true);
        break;
      case 'mousemove':
      case 'touchmove':
        this.smooth.target =
          this.downTarget + (((x - this.downX) * this.options.dragMultiplier) / this.width) * -1;
        break;
      case 'mouseup':
      case 'touchend':
      case 'mouseleave':
      case 'blur':
        if (isLink) {
          const link = isTargetLink ? $node : $node.closest('a');
          const dX = Math.abs(this.downX - x);
          const dY = Math.abs(this.downY - y);
          const time = Date.now() - this.downTime;
          if (link === this.downLink && dX < 20 && dY < 20 && time < 300) {
            link.click();
          }
        }

        delete this.downLink;
        delete this.downTime;

        const { target, options } = this.smooth;
        let roundedTarget = target;
        roundedTarget = Math.max(roundedTarget, options.min);
        roundedTarget = Math.min(roundedTarget, options.max);

        const roundedIndex = Math.round(roundedTarget);
        this.goTo(roundedIndex);

        this.bindDownEvents(false);
        break;
    }
  }

  bindDownEvents(enable) {
    const events = ['mousemove', 'mouseup', 'mouseleave', 'blur', 'touchmove', 'touchend'];
    const method = enable ? 'addEventListener' : 'removeEventListener';
    events.forEach((event) => window[method](event, this.handleEvent));
  }

  handleBreakpoint(event) {
    this.options.onBreakpointChange(this.mediaQueryLists, this);
  }

  handleUpdate(value, force = false) {
    for (var i = this.$items.length - 1; i >= 0; i--) {
      this.$items[i].style.transform = `translateX(${-value * this.options.groupBy * 100}%)`;
      this.$items[i].style.willChange = `transform`;
    }
  }

  handleUpdateEnd() {
    for (var i = this.$items.length - 1; i >= 0; i--) {
      this.$items[i].style.removeProperty('will-change');
    }
  }

  handleResize() {
    if (!this.$wrapper || this.$wrapper.children.length <= 0) return;

    this.width = this.$element.offsetWidth;
    this.height = this.$element.offsetHeight;
    this.$items.forEach(($node) => $node.style.removeProperty('transform'));

    requestAnimationFrame(this.handleResized);
  }

  handleResized() {
    this.handleUpdate(this.smooth.value, true);
  }

  handleKeyUp({ keyCode }) {
    switch (keyCode) {
      case 37:
        return this.goTo(this.index - 1);
      case 39:
        return this.goTo(this.index + 1);
    }
  }

  goTo(index, force = false) {
    const max = Math.ceil(this.length / this.options.groupBy) - 1;
    index = index < 0 ? 0 : index;
    index = index > max ? 0 : index;

    this.smooth.target = index;

    this.index = index;

    if (force) {
      this.smooth.set(-index);
    }
  }

  set index(groupIndex) {
    this._index = groupIndex;
    const maxIndex = Math.ceil(this.length / this.options.groupBy) - 1;
    const itemIndex = groupIndex * this.options.groupBy;

    this.$element.classList.remove('is-first-item');
    this.$element.classList.remove('is-last-item');

    this.$items.forEach(($item, i) => {
      const isActive = itemIndex <= i && i < itemIndex + this.options.groupBy;
      $item.classList[isActive ? 'add' : 'remove']('is-active');
    });

    if (this.$dots[groupIndex]) {
      this.$dots.forEach(($dot) => $dot.classList.remove('is-active'));
      this.$dots[groupIndex].classList.add('is-active');
    }

    if (groupIndex === 0) {
      this.$element.classList.add('is-first-item');
    } else if (groupIndex === maxIndex) {
      this.$element.classList.add('is-last-item');
    }
  }

  get index() {
    return this._index;
  }

  set groupBy(value) {
    this.options.groupBy = value;
    this.smooth.options.max = Math.max(0, Math.ceil(this.length / value) - 1);

    this.$dots.forEach(($dot, index) => {
      if (index <= this.smooth.options.max) {
        $dot.style.removeProperty('display');
      } else {
        $dot.style.display = 'none';
      }
    });

    const $itemsToToggleDisplay = [
      ...this.$element.querySelectorAll('.carousel__zones,.carousel__dots'),
    ];
    if (this.length <= value) {
      $itemsToToggleDisplay.forEach(($el) => ($el.style.display = 'none'));
    } else {
      $itemsToToggleDisplay.forEach(($el) => $el.style.removeProperty('display'));
    }
  }

  get groupBy() {
    return this.options.groupBy;
  }
}

export default Carousel;
